/**
 * @module windowShapes
 * @description Window elevation shape geometry
 */
import { openingSideOffset2 } from "building-common/openings/openingQuery";
import { profile2OrientCopy } from "building-common/profile2Utils";
import type { OpeningSideHolePlacement } from "building-types/openings/opening";
import { type OpeningSide, type OpeningShapes, type OpeningSideContext } from "building-types/openings/openingTypes";
import type { ProfilesWithDepth } from "building-types/profiles/profileTypes";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import type {
  HeadCasingArchedSlices,
  HeadCasingEyebrowTrim,
  HeadCasingFullArch,
  HeadCasingJackArch,
  HeadCasingSettings,
  HeadCasingSlices,
  HeadCasingTrim,
} from "building-types/settings/headCasingSettings";
import { HeadCasingType, SideCasingType } from "building-types/settings/settingsTypesEnum";
import { PolygonWindowMode, WindowPanelType, WindowShapeTypes } from "building-types/settings/windowTypesEnum";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import { BuiltinWindowId } from "building-types/windows/windowId";
import type {
  GeneratedWindowPlacement,
  GeneratedWindowPlacementV2,
  GeneratedWindowPlacementDeprecated,
  WindowDescriptor,
  MergedWindowSettings,
} from "building-types/windows/windowTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { lerp } from "common/utils/math";
import { levelInchesNew } from "common/utils/nominalNumbers";
import uid from "common/utils/uid";
import { WINDOW_EXTERIOR_FRAME_DEPTH, WINDOW_INTERIOR_FRAME_WIDTH } from "doc/elements/window/constants";
import { roomIsEffectivelyExterior } from "doc/rooms/roomQuery";
import { isExteriorWindowCasingSettings } from "doc/settingsTools";
import { arc2ByCenterStartAngle } from "geom/arc";
import { closestParamNormalizedLine2, closestParamLine2 } from "geom/closestPoint";
import { PI, DIST_EPSILON } from "geom/core";
import { offsetPolyLine2, polyLine2ThickenedEdge } from "geom/offset";
import { planeCopy, polyLine2OnPlane } from "geom/plane";
import {
  polyLine2SetCCW,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2ReverseCopy,
  polyLine2Shift,
  polyLine2EdgeTangent,
  polyLine2Length,
  polyLine2EdgeEval,
  polyLine2Start,
  polyLine2End,
  polyLine2IsClosedRect,
  polyLine2MoveEdges,
  polyLine2ConcatAll,
  polyLine2Simplify,
  polyLine2Close,
  polyLine2EdgeLength,
  polyLine2EdgeCount,
  polyLine2NonUniformScaleAndKeepCenter,
  polyLine2AABB2,
  polyLine2Move,
  polyLine2SetCW,
  polyLine2Reverse,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLineEdgeType } from "geom/polyLineTypes";
import { makeRectFromLine } from "geom/rect2";
import type { Rect2 } from "geom/rect2Types";
import { vec2Dist, vec2Dot, vec2Lerp } from "geom/vec2";
import { mirrorProfile2Copy } from "../../utils/mirror";
import {
  headCasingArchedSlicesProfiles,
  headCasingTrimProfile,
  headCasingStoneProfile,
  headCasingJackArchProfiles,
  headCasingEyebrowTrimProfiles,
  headCasingFullArchProfiles,
  headCasingSidingProfiles,
  sideCasingProfile,
  sideCasingProfileEdgeTypes,
} from "../casing/casingProfiles";
import { headCasingRemoveCorner } from "../casing/casingQuery";
import { getArcCenter } from "../doors/doorUtils";
import { offsetCasingShapes } from "../openings/offsetCasing";
import { makeOpeningSide, openingProfileFromSections } from "../openings/openingProfile";
import { rectilinearOpeningProfile, unionedProfiles } from "../openings/openingUtils";
import { mirrorProfiles, reprojectProfiles } from "../walls/openingProfiles";
import { wallBaseLine } from "../walls/wallBaseline";
import { getWindowHeight, windowHeadHeight } from "./windowHeightTools";
import {
  windowHasApronCasing,
  windowHasCasing,
  windowHasHeadCasing,
  windowHasSideCasing,
  windowHasStoolCasing,
  windowHasUnsupportedHeadCasing,
} from "./windowQuery";
import { archRadiusEqualsWindowWidth, windowConstrainAngleVerticalOffset } from "./windowUtils";

export const WINDOW_CHORD_TOLERANCE = 0.01;
export const WINDOW_ARC_MIN_DIVS = 10;
export const WINDOW_ARC_MAX_DIVS = 15;

function curvedProfileEdgeTypes(profile: PolyLine2): readonly PolyLineEdgeType[] {
  return Array<PolyLineEdgeType>(polyLine2EdgeCount(profile)).fill("CURVED");
}

function curvedProfileWithStraightEndsEdgeTypes(profile: PolyLine2): readonly PolyLineEdgeType[] {
  const edgeTypes = [...curvedProfileEdgeTypes(profile)];
  if (edgeTypes.length > 0) {
    edgeTypes[0] = "STRAIGHT";
    edgeTypes[edgeTypes.length - 1] = "STRAIGHT";
  }
  return edgeTypes;
}

export function makeWindowSide(
  context: OpeningSideContext,
  placement: OpeningSideHolePlacement,
  profileRectangular: OpeningSide,
  window: GeneratedWindowPlacement,
  settings: MergedWindowSettings,
  mirror: boolean,
): OpeningSide {
  let { condition } = context;
  const facePlane = planeCopy(placement.openingPlane);

  const profileProjected = reprojectProfiles(profileRectangular, placement.edgePlane, facePlane);

  let resolvedProfileRectangular: OpeningSide = profileRectangular;
  const backOpening: PolyLine2 = profileProjected.opening2;

  const shapes = windowShapes(window.descriptor, backOpening);

  if (condition === undefined) {
    condition = roomIsEffectivelyExterior(placement.room, false) ? "EXTERIOR" : "INTERIOR";
  }

  const isExterior = condition === "EXTERIOR";
  const casingSettings = isExterior ? settings.exteriorCasing : settings.interiorCasing;

  const { outline, outlineRectangular, arcCurveSimple, arcCurveSimpleEdgeTypes, outlineReversed } = shapes;

  const hasCasing = windowHasCasing(window, casingSettings);

  let side = makeOpeningSide({
    hasCasing,
    openingRect2: outlineRectangular,
    shapes,
    opening2: profile2OrientCopy(polyLine2SetCCW(outline)),
    opening2EdgeTypes: shapes.outlineEdgeTypes,
    opening3: polyLine2OnPlane(facePlane, outline),
    context: { ...context, condition },
  });

  if (mirror) {
    side = mirrorProfiles(side);
  }

  if (!isExterior) {
    // @TODO(Andrew): calculate actual interior casing profiles here
    // @NOTE(Andrew): setting this as a copy causes gable ends to disappear(???)
    side.openingCasing2 = profile2OrientCopy(polyLine2SetCCW(side.opening2));
    if (settings.interiorCasing?.includeCasing) {
      side.trim.push({
        id: uid(),
        type: "SIDE_CASING",
        name: "interior",
        rail: polyLine2Copy(side.opening2),
        elevationProfile: [
          polyLine2SetCW(offsetPolyLine2(side.opening2, WINDOW_INTERIOR_FRAME_WIDTH)),
          polyLine2Copy(side.opening2),
        ],
        width: WINDOW_INTERIOR_FRAME_WIDTH,
        depth: WINDOW_EXTERIOR_FRAME_DEPTH,
      });
    }
    return side;
  }

  if (!hasCasing || !outlineReversed || !isExteriorWindowCasingSettings(casingSettings)) {
    return side;
  }

  const { sideCasingWidth = 0, casingHorizontalOffset = 0 } = casingSettings;
  let { arcCurve, arcCurveEdgeTypes } = shapes;

  const trimExtend = casingSettings.sideCasingType === SideCasingType.DISABLED ? 0 : sideCasingWidth;

  if (casingHorizontalOffset !== 0) {
    const offsetShapes = offsetCasingShapes(casingHorizontalOffset, {
      arcCurve,
      arcCurveSimple,
      casingOutline: outlineReversed,
    });
    arcCurve = offsetShapes.arcCurve;
    arcCurveEdgeTypes = arcCurve && curvedProfileWithStraightEndsEdgeTypes(arcCurve);
    shapes.arcCurve = arcCurve;
    shapes.arcCurveEdgeTypes = arcCurveEdgeTypes;
    shapes.arcCurveSimple = offsetShapes.arcCurveSimple;
    shapes.arcCurveSimpleEdgeTypes = offsetShapes.arcCurveSimple && curvedProfileEdgeTypes(offsetShapes.arcCurveSimple);
  }
  const outline2ForCasing = polyLine2Copy(openingSideOffset2(side, -casingHorizontalOffset));

  if (windowHasApronCasing(window, casingSettings.apronCasingType)) {
    const apronLine = polyLine2Reverse(polyLine2Edge(outline2ForCasing, 0));

    const { apronHeight = 0, apronDepth = 0, apronExtension = 0, apronEndSlope = 1 } = casingSettings;

    const profile = apronCasingProfile(apronLine, apronHeight, apronExtension, apronEndSlope, trimExtend);

    side.trim.push({
      id: uid(),
      type: "APRON",
      name: "apron",
      rail: apronLine,
      width: apronHeight,
      depth: apronDepth,
      elevationProfile: [profile],
    });

    side.apronCasing = profile;
  }

  if (windowHasSideCasing(casingSettings)) {
    const {
      sideCasingWidth = 0,
      sideCasingDepth = WINDOW_EXTERIOR_FRAME_DEPTH,
      sideCasingTopOffset = 0,
      sideCasingBottomOffset = 0,
      sideCasingMaterial,
    } = casingSettings;
    switch (window.descriptor.shape) {
      case WindowShapeTypes.RECTANGLE:
      case WindowShapeTypes.RECTANGLE_ARCHED_TOP:
        const leftEdge = polyLine2Edge(outline2ForCasing, -1);
        const leftProfile = sideCasingProfile(leftEdge, sideCasingWidth, -sideCasingBottomOffset, sideCasingTopOffset);
        side.sideCasing.push(leftProfile);
        const rightEdge = polyLine2Edge(outline2ForCasing, 1);
        const rightProfile = sideCasingProfile(
          rightEdge,
          sideCasingWidth,
          sideCasingTopOffset,
          -sideCasingBottomOffset,
        );
        side.sideCasing.push(rightProfile);
        side.trim.push(
          {
            id: uid(),
            type: "SIDE_CASING",
            name: "right",
            width: sideCasingWidth,
            depth: sideCasingDepth,
            rail: rightEdge,
            elevationProfile: rightProfile,
            materialId: sideCasingMaterial,
          },
          {
            id: uid(),
            type: "SIDE_CASING",
            name: "left",
            width: sideCasingWidth,
            depth: sideCasingDepth,
            rail: leftEdge,
            elevationProfile: leftProfile,
            materialId: sideCasingMaterial,
          },
        );
        break;
      case WindowShapeTypes.POLYGONAL:
        const upperEdges = outline2ForCasing.slice(2);
        const polygonalProfile = sideCasingProfile(
          upperEdges,
          sideCasingWidth,
          -sideCasingBottomOffset,
          -sideCasingBottomOffset,
        );
        side.sideCasing.push(polygonalProfile);
        side.trim.push({
          id: uid(),
          type: "SIDE_CASING",
          name: "polygonal",
          width: sideCasingWidth,
          depth: sideCasingDepth,
          rail: upperEdges,
          elevationProfile: polygonalProfile,
          materialId: sideCasingMaterial,
        });
        break;
      default:
        const profile = sideCasingProfile(outline2ForCasing, sideCasingWidth);
        const elevationProfileEdgeTypes =
          side.opening2EdgeTypes && sideCasingProfileEdgeTypes(profile, side.opening2EdgeTypes);
        side.sideCasing.push(profile);
        side.trim.push({
          id: uid(),
          type: "SIDE_CASING",
          name: "irregular",
          width: sideCasingWidth,
          depth: sideCasingDepth,
          rail: outline2ForCasing,
          elevationProfile: profile,
          elevationProfileEdgeTypes,
          materialId: sideCasingMaterial,
        });
        break;
    }
  }

  if (windowHasHeadCasing(window, casingSettings.headCasingType)) {
    const { headCasingType, sideCasingType } = casingSettings;
    const { descriptor: windowDescriptor } = window;
    const {
      descriptor: { shape: windowShape },
    } = window;
    //get generation geometry
    let headcasingLine = polyLine2Reverse(polyLine2Edge(outline2ForCasing, 2));

    polyLine2ExtendEnd(headcasingLine, trimExtend);
    polyLine2ExtendStart(headcasingLine, trimExtend);

    const backPlane = planeCopy(facePlane);

    //full arch errors
    let arcCurveError = false;
    if ((casingSettings as HeadCasingSettings)?.headCasingType === HeadCasingType.FULL_ARCH) {
      if (!arcCurve) {
        arcCurveError = true;
      } else {
        arcCurve = polyLine2ReverseCopy(arcCurve);
      }
    }

    //unsuppored headcasing for window shape
    let unsupportedHeadCasing = windowHasUnsupportedHeadCasing(window, casingSettings);

    if (windowShape === WindowShapeTypes.POLYGONAL) {
      if (headCasingType !== HeadCasingType.TRIM) {
        unsupportedHeadCasing = true;
      }
      if (windowDescriptor.polygonWindowMode === PolygonWindowMode.PENTAGONAL) {
        const pl = polyLine2Copy(outline2ForCasing);
        headcasingLine = polyLine2New([pl[4], pl[5], pl[6], pl[7], pl[8], pl[9]]);
      }
    }

    // @TODO(Andrew): another example of reversed geometry that needs to be resolved
    if (arcCurve && !arcCurveError && headCasingType === HeadCasingType.FULL_ARCH) {
      headcasingLine = polyLine2ReverseCopy(arcCurve);
      resolvedProfileRectangular = {
        ...resolvedProfileRectangular,
        opening2: profile2OrientCopy(polyLine2ReverseCopy(resolvedProfileRectangular.opening2)),
      };
    }

    let useArcCurveSimple = false;
    if (sideCasingType === SideCasingType.DISABLED) {
      if (headCasingRemoveCorner(casingSettings) && !archRadiusEqualsWindowWidth(windowDescriptor)) {
        useArcCurveSimple = true;
      }
    }

    if (arcCurveError) {
      console.warn("Window Full Arc curve is null and cannot produce FULL_ARCH_CASING!");
    } else if (!unsupportedHeadCasing) {
      let profilesWithDepth: ProfilesWithDepth | undefined = undefined;
      switch (headCasingType) {
        case HeadCasingType.STONE:
          const stoneProfile = headCasingStoneProfile(headcasingLine, casingSettings);
          side.headCasing.push(stoneProfile);
          side.trim.push({
            id: uid(),
            type: "HEAD_CASING",
            name: "stone",
            rail: headcasingLine,
            width: casingSettings.headCasingHeight,
            depth: casingSettings.headCasingDepth,
            elevationProfile: [stoneProfile],
          });
          break;
        case HeadCasingType.TRIM:
          const curve = useArcCurveSimple ? arcCurveSimple : arcCurve;
          const curveEdgeTypes = useArcCurveSimple ? arcCurveSimpleEdgeTypes : arcCurveEdgeTypes;
          profilesWithDepth = headCasingTrimProfile(
            headcasingLine,
            curve,
            casingSettings as HeadCasingTrim,
            curveEdgeTypes,
          );
          break;
        case HeadCasingType.JACK_ARCH:
          profilesWithDepth = headCasingJackArchProfiles(
            headcasingLine,
            backPlane,
            casingSettings as HeadCasingJackArch,
          );
          break;
        case HeadCasingType.FULL_ARCH:
          profilesWithDepth = headCasingFullArchProfiles(
            headcasingLine,
            resolvedProfileRectangular.opening2,
            casingSettings as HeadCasingFullArch,
            // @TODO(Andrew): the default will eventually be DIST_Z_FIGHT and this can be omitted
            0,
          );
          break;

        case HeadCasingType.EYEBROW_TRIM:
          profilesWithDepth = headCasingEyebrowTrimProfiles(headcasingLine, casingSettings as HeadCasingEyebrowTrim);
          break;
        case HeadCasingType.ARCHED_SLICES:
          profilesWithDepth = headCasingArchedSlicesProfiles(headcasingLine, casingSettings as HeadCasingArchedSlices);
          break;
        case HeadCasingType.SLICES:
          profilesWithDepth = headCasingSidingProfiles(headcasingLine, casingSettings as HeadCasingSlices);
          break;
        case HeadCasingType.NONE:
        default:
      }

      if (profilesWithDepth) {
        side.headHeight = profilesWithDepth.depth;
        for (const profile of profilesWithDepth.profiles) {
          side.headCasing.push(profile);
        }
        side.trim.push(...profilesWithDepth.trim);
      }
    }
  }

  if (windowHasStoolCasing(window, casingSettings.stoolCasingType)) {
    const stoolLine = polyLine2Reverse(polyLine2Edge(outline2ForCasing, 0));

    const { stoolCasingThickness = 0, stoolCasingExtension = 0 } = casingSettings;

    const profile = stoolCasingProfile(stoolLine, stoolCasingThickness, stoolCasingExtension, trimExtend);

    side.trim.push({
      id: uid(),
      type: "STOOL",
      name: "stool",
      rail: stoolLine,
      depth: stoolCasingThickness,
      elevationProfile: [profile],
    });

    side.stoolCasing = profile;
  }

  // @TODO(Andrew): memoize this
  const unioned = unionedProfiles(side, casingSettings);
  if (unioned.length > 0) {
    side.openingCasing2 = profile2OrientCopy(polyLine2SetCCW(unioned[0]));
  }

  return side;
}

export function apronCasingProfile(
  casingLine: PolyLine2,
  apronHeight: number,
  apronExtension: number = 0,
  apronEndSlope: number,
  trimExtend: number,
): PolyLine2 {
  polyLine2ExtendStart(casingLine, trimExtend + apronExtension);
  polyLine2ExtendEnd(casingLine, trimExtend + apronExtension);
  const apronBase = makeRectFromLine(casingLine, 0, apronHeight);

  if (apronEndSlope !== 0) {
    const slopeOffset = (1 / apronEndSlope) * apronHeight;
    apronBase[2] -= slopeOffset;
    apronBase[4] += slopeOffset;
  }

  return apronBase;
}

export function stoolCasingProfile(
  casingLine: PolyLine2,
  stoolCasingThickness: number,
  stoolCasingExtension: number = 0,
  trimExtend: number,
): PolyLine2 {
  polyLine2ExtendStart(casingLine, trimExtend + stoolCasingExtension);
  polyLine2ExtendEnd(casingLine, trimExtend + stoolCasingExtension);
  return makeRectFromLine(casingLine, 0, stoolCasingThickness);
}

export function windowRectangularOpening(
  window: GeneratedWindowPlacement,
  frontEdge: PolyLine2 | undefined,
  frontEdgeLength: number,
  wallMassSystem: WallMassSystem | undefined,
): OpeningSide | null {
  switch (window.type) {
    case "WINDOW": {
      return windowDeprecatedRectangularOpening(window, frontEdge, frontEdgeLength, wallMassSystem);
    }
    case "WINDOW_V2": {
      return windowV2RectangularOpening(window, frontEdge);
    }
  }
}

function windowDeprecatedRectangularOpening(
  window: GeneratedWindowPlacementDeprecated,
  frontEdge: PolyLine2 | undefined,
  frontEdgeLength: number,
  wallMassSystem: WallMassSystem | undefined,
): OpeningSide | null {
  let centerParam = window.param;
  let centerStripParam = centerParam;
  let stripLength = frontEdgeLength;

  if (window.wallStripDomain) {
    assertIsNotNullOrUndefined(wallMassSystem);
    const wallIndex = wallMassSystem.wallIndex;
    const wall = wallIndex.walls[wallMassSystem.halfEdgeToWall[window.halfEdgeIndex]];

    assertIsNotNullOrUndefined(window.frontRoom);
    const wallsInRoom = wallIndex.wallsInRoom(window.frontRoom);
    const wallBL = wallBaseLine(window.frontRoom, wallsInRoom, wallsInRoom.indexOf(wall));

    assertIsNotNullOrUndefined(frontEdge);
    const [facedirx, facediry] = polyLine2EdgeTangent(frontEdge, 0);

    const wallStrip = wallIndex.parentWallStrip(window.frontRoom, wall);
    if (!wallStrip) {
      return null;
    }
    const wallStripBL = wallStrip.baselineMaxOffset(true);
    const [wallstx, wallsty] = polyLine2EdgeTangent(wallStripBL, 0);
    stripLength = polyLine2Length(wallStripBL);

    // evaluate point on wall strip base line

    let wallStripParam = window.param;

    if (wallStripParam < 0) {
      if (window.paramNormalized) {
        wallStripParam = wallStripParam + 1.0;
      } else {
        wallStripParam = wallStripParam + stripLength;
      }
    }

    const [x, y] = polyLine2EdgeEval(wallStripBL, 0, wallStripParam, window.paramNormalized);

    assertIsNotNullOrUndefined(wallBL);

    centerParam = window.paramNormalized
      ? closestParamNormalizedLine2(wallBL[0], wallBL[1], wallBL[2], wallBL[3], x, y, false)
      : closestParamLine2(wallBL[0], wallBL[1], wallBL[2], wallBL[3], x, y, false);

    centerStripParam = window.paramNormalized
      ? closestParamNormalizedLine2(wallStripBL[0], wallStripBL[1], wallStripBL[2], wallStripBL[3], x, y, false)
      : closestParamLine2(wallStripBL[0], wallStripBL[1], wallStripBL[2], wallStripBL[3], x, y, false);

    if (window.paramNormalized && (centerStripParam > 1.0 || centerStripParam < 0.0)) {
      return null;
    } else if (centerStripParam > stripLength || centerStripParam < 0) {
      return null;
    }

    // reverse the direction of the param if it doesn't align with the room bounday
    if (vec2Dot(wallstx, wallsty, facedirx, facediry) < 0) {
      centerStripParam = -centerStripParam;
      centerParam = -centerParam;
    }
  }

  if (window.paramNormalized) {
    centerParam = frontEdgeLength * centerParam;
    centerStripParam = stripLength * centerStripParam;
  }

  if (centerParam < 0) {
    centerParam = frontEdgeLength + centerParam;
  }

  if (centerStripParam < 0) {
    centerStripParam = stripLength + centerStripParam;
  }

  let width = window.descriptor.width;

  if (window.descriptor.id === BuiltinWindowId.S1) {
    width = levelInchesNew(stripLength - 42);
  }

  const halfWidth = width / 2;
  const panelType = window.materials?.panelType;
  const startStripParam = panelType === WindowPanelType.FAUX_SHUTTER ? centerParam : centerStripParam - halfWidth;
  const endStripParam = panelType === WindowPanelType.FAUX_SHUTTER ? centerParam : centerStripParam + halfWidth;

  if (endStripParam > stripLength || endStripParam < 0 || startStripParam > stripLength || startStripParam < 0) {
    return null;
  }

  return rectangularProfileData(window, centerParam);
}

function windowV2RectangularOpening(
  window: GeneratedWindowPlacementV2,
  frontEdge: PolyLine2 | undefined,
): OpeningSide | null {
  const { x, y } = window;
  if (x === undefined || y === undefined) {
    return null;
  }

  assertIsNotNullOrUndefined(frontEdge);
  const [sx, sy] = polyLine2Start(frontEdge);
  const [ex, ey] = polyLine2End(frontEdge);

  const t = closestParamLine2(sx, sy, ex, ey, x, y, false);
  const centerParam = t;

  return rectangularProfileData(window, centerParam);
}

function rectangularProfileData(window: GeneratedWindowPlacement, centerParam: number) {
  const top = levelInchesNew(windowHeadHeight(window));
  const bottom = levelInchesNew(top - getWindowHeight(window.descriptor));
  const width = window.descriptor.width;

  const halfWidth = width / 2;
  const startParam = centerParam - halfWidth;
  const endParam = centerParam + halfWidth;

  return makeOpeningSide({
    ...rectilinearOpeningProfile({
      start: startParam,
      bottom,
      end: endParam,
      top,
      streetOrientation: window.streetOrientation,
      exteriorCasingSettings: window.exteriorCasing as ExteriorWindowCasingSettings,
    }),
    startParam,
    endParam,
  });
}

/**
 * @param descriptor
 * @param outlineRect rectangular outline of the window
 * @param mirror currently only used for polygonal windows
 * @returns
 */
export function windowShapes(descriptor: WindowDescriptor, outlineRect: Rect2, mirror: boolean = false): OpeningShapes {
  if (!(polyLine2IsClosedRect(outlineRect))) {
    console.assert(false, "Window exterior outline must be a rectangle");
  }

  let partialShapes: OpeningShapes;
  switch (descriptor.shape) {
    case WindowShapeTypes.CIRCLE: {
      partialShapes = circleWindowShapes(descriptor, outlineRect);
      break;
    }
    case WindowShapeTypes.HALF_ROUND: {
      partialShapes = halfRoundWindowShapes(descriptor, outlineRect);
      break;
    }
    case WindowShapeTypes.ELLIPSE: {
      partialShapes = ellipseWindowShapes(outlineRect);
      break;
    }
    case WindowShapeTypes.POLYGONAL: {
      partialShapes = polygonalWindowShapes(descriptor, outlineRect, mirror);
      break;
    }
    case WindowShapeTypes.RECTANGLE_ARCHED_TOP: {
      partialShapes = archedRectangleWindowShapes(descriptor, outlineRect);
      break;
    }
    case WindowShapeTypes.RECTANGLE:
    default: {
      partialShapes = rectangleWindowShapes(descriptor, outlineRect);
      break;
    }
  }
  const outlineReversed = profile2OrientCopy(polyLine2SetCW(polyLine2Copy(partialShapes.outline)));
  return {
    ...partialShapes,
    outlineReversed,
  };
}

export function windowShapesCopy(shapes: OpeningShapes): OpeningShapes {
  return {
    ...shapes,
    outlineEdgeTypes: [...shapes.outlineEdgeTypes],
    arcCurveEdgeTypes: shapes.arcCurveEdgeTypes && [...shapes.arcCurveEdgeTypes],
    arcCurveSimpleEdgeTypes: shapes.arcCurveSimpleEdgeTypes && [...shapes.arcCurveSimpleEdgeTypes],
    outline: polyLine2Copy(shapes.outline),
    outlineRectangular: polyLine2Copy(shapes.outlineRectangular),
    outlineReversed: shapes.outlineReversed && polyLine2Copy(shapes.outlineReversed),
    arcCurve: shapes.arcCurve && polyLine2Copy(shapes.arcCurve),
    arcCurveSimple: shapes.arcCurveSimple && polyLine2Copy(shapes.arcCurveSimple),
    arcOutline: shapes.arcOutline && polyLine2Copy(shapes.arcOutline),
    arcOutlineReversed: shapes.arcOutlineReversed && polyLine2Copy(shapes.arcOutlineReversed),
  };
}

function rectangleWindowShapes(descriptor: WindowDescriptor, outlineRectangular: Rect2): OpeningShapes {
  let arcCurve: PolyLine2 | undefined;
  let arcCurveWithEnds: PolyLine2 | undefined;
  let arcOutline: PolyLine2 | undefined;
  let arcRadius = 0;
  let arcAngleRads = 0;
  let arcCenterX = 0;
  let arcCenterY = 0;
  let arcHeight = 0;
  let archOutlineReversed: PolyLine2 | undefined;

  let outline = polyLine2Copy(outlineRectangular);
  let outlineEdgeTypes = openingProfileFromSections([{ polyLine: outline, edgeType: "STRAIGHT" }]).outlineEdgeTypes;

  if (descriptor.arched) {
    const topArchGeom = archGeom(descriptor, outlineRectangular);
    const { arcStartX, arcStartY, arcEndX, arcEndY } = topArchGeom;
    ({ arcCurve, arcRadius, arcAngleRads, arcCenterX, arcCenterY, arcHeight } = topArchGeom);

    outline = polyLine2MoveEdges(polyLine2Copy(outlineRectangular), [2], 0, -arcHeight);

    arcCurveWithEnds = polyLine2Simplify(
      polyLine2ConcatAll([
        [arcStartX, arcStartY - 1],
        [arcStartX, arcStartY],
        arcCurve.slice(2, arcCurve.length - 2),
        [arcEndX, arcEndY],
        [arcEndX, arcEndY - 1],
      ]),
    );
    arcOutline = polyLine2Close(arcCurveWithEnds);

    archOutlineReversed = arcOutline && polyLine2ReverseCopy(arcOutline);

    const profile = openingProfileFromSections([
      { polyLine: polyLine2New([outline[0], outline[1], outline[2], outline[3]]), edgeType: "STRAIGHT" },
      {
        polyLine: polyLine2New([outline[2], outline[3], arcCurve[0], arcCurve[1]]),
        edgeType: "STRAIGHT",
      },
      { polyLine: arcCurve, edgeType: "CURVED" },
      {
        polyLine: polyLine2New([arcCurve[arcCurve.length - 2], arcCurve[arcCurve.length - 1], outline[0], outline[1]]),
        edgeType: "STRAIGHT",
      },
    ]);
    outline = profile.outline;
    outlineEdgeTypes = profile.outlineEdgeTypes;
  }

  return {
    outlineRectangular,
    outline,
    outlineEdgeTypes,
    arcCurve: arcCurveWithEnds,
    arcCurveEdgeTypes: arcCurveWithEnds && curvedProfileWithStraightEndsEdgeTypes(arcCurveWithEnds),
    arcCurveSimple: arcCurve,
    arcCurveSimpleEdgeTypes: arcCurve && curvedProfileEdgeTypes(arcCurve),
    arcOutline,
    arcOutlineReversed: archOutlineReversed,
    arcHeight,
    arcAngleRads,
    arcCenterX,
    arcCenterY,
    arcRadius,
  };
}

export function archedRectangleWindowShapes(descriptor: WindowDescriptor, outlineRectangular: Rect2): OpeningShapes {
  const topArchGeom = archGeom(descriptor, outlineRectangular);
  const { arcRadius, arcAngleRads, arcCenterX, arcCenterY, arcHeight, arcStartX, arcStartY, arcEndX, arcEndY } =
    topArchGeom;
  const { arcCurve } = topArchGeom;
  const arcEndOffset = arcCurve.length - 2;

  const { outline, outlineEdgeTypes } = openingProfileFromSections([
    {
      polyLine: polyLine2New([
        outlineRectangular[0],
        outlineRectangular[1],
        outlineRectangular[2],
        outlineRectangular[3],
      ]),
      edgeType: "STRAIGHT",
    },
    {
      polyLine: polyLine2New([outlineRectangular[2], outlineRectangular[3], arcCurve[0], arcCurve[1]]),
      edgeType: "STRAIGHT",
    },
    { polyLine: arcCurve, edgeType: "CURVED" },
    {
      polyLine: polyLine2New([
        arcCurve[arcEndOffset],
        arcCurve[arcEndOffset + 1],
        outlineRectangular[0],
        outlineRectangular[1],
      ]),
      edgeType: "STRAIGHT",
    },
  ]);

  const arcCurveWithEnds = polyLine2Simplify(
    polyLine2ConcatAll([
      [arcStartX, arcStartY - 1],
      [arcStartX, arcStartY],
      arcCurve,
      [arcEndX, arcEndY],
      [arcEndX, arcEndY - 1],
    ]),
  );

  return {
    outlineRectangular,
    outline,
    outlineEdgeTypes,
    arcCurve: arcCurveWithEnds,
    arcCurveEdgeTypes: curvedProfileWithStraightEndsEdgeTypes(arcCurveWithEnds),
    arcCurveSimple: arcCurve,
    arcCurveSimpleEdgeTypes: curvedProfileEdgeTypes(arcCurve),
    arcHeight,
    arcAngleRads,
    arcCenterX,
    arcCenterY,
    arcRadius,
  };
}

function circleWindowShapes(descriptor: WindowDescriptor, outlineRectangular: Rect2): OpeningShapes {
  //top arch
  const topArchGeom = archGeom(descriptor, outlineRectangular);
  const { arcRadius, arcAngleRads, arcCenterX, arcCenterY, arcHeight } = topArchGeom;
  const topArcCurve = topArchGeom.arcCurve;

  //bottom arc
  const bottomArchExteriorOutline = polyLine2Shift(polyLine2Copy(outlineRectangular), 2);
  const bottomArchGeom = archGeom(descriptor, bottomArchExteriorOutline);
  const bottomArcCurve = bottomArchGeom.arcCurve;

  //circle outline - remove ends to avoid overlap
  const circleOutline = polyLine2Close(
    polyLine2Simplify(polyLine2New([...topArcCurve.slice(2), ...bottomArcCurve.slice(2)])),
  );
  const { outline, outlineEdgeTypes } = openingProfileFromSections([{ polyLine: circleOutline, edgeType: "CURVED" }]);

  return {
    outlineRectangular,
    outline,
    outlineEdgeTypes,
    arcHeight,
    arcAngleRads,
    arcCenterX,
    arcCenterY,
    arcRadius,
  };
}

function halfRoundWindowShapes(descriptor: WindowDescriptor, outlineRectangular: Rect2): OpeningShapes {
  //top arch geom
  const topArchGeom = archGeom(descriptor, outlineRectangular);
  const { arcCurve, arcRadius, arcAngleRads, arcCenterX, arcCenterY, arcHeight } = topArchGeom;

  //half round outline
  const arcEndOffset = arcCurve.length - 2;
  const { outline, outlineEdgeTypes } = openingProfileFromSections([
    {
      polyLine: polyLine2New([arcCurve[arcEndOffset], arcCurve[arcEndOffset + 1], arcCurve[0], arcCurve[1]]),
      edgeType: "STRAIGHT",
    },
    { polyLine: arcCurve, edgeType: "CURVED" },
  ]);

  return {
    outlineRectangular,
    outline,
    outlineEdgeTypes,
    arcCurve,
    arcCurveEdgeTypes: curvedProfileEdgeTypes(arcCurve),
    arcHeight,
    arcAngleRads,
    arcCenterX,
    arcCenterY,
    arcRadius,
  };
}

function ellipseWindowShapes(outlineRectangular: Rect2): OpeningShapes {
  //overall dims
  const width = polyLine2EdgeLength(outlineRectangular, 2);
  const height = polyLine2EdgeLength(outlineRectangular, 1);
  const radius = Math.max(width / 2, height / 2);

  //scale factor
  const scaleX = height > width;
  const ellipseScaleXFactor = scaleX ? width / height : 1;
  const ellipseScaleYFactor = scaleX ? 1 : height / width;

  //ellipse center point
  const edge = scaleX ? polyLine2Edge(outlineRectangular, 2) : polyLine2Edge(outlineRectangular, 1);
  let [centerX, centerY] = vec2Lerp(edge[0], edge[1], edge[2], edge[3], 0.5);
  centerX += scaleX ? 0 : -radius;
  centerY += scaleX ? -radius : 0;

  //make ellipse as 2 halves
  const startX1 = scaleX ? centerX + radius : centerX;
  const startY1 = scaleX ? centerY : centerY + radius;
  const startX2 = scaleX ? centerX - radius : centerX;
  const startY2 = scaleX ? centerY : centerY - radius;

  const makeEllipse = ellipseMaker(centerX, centerY, ellipseScaleXFactor, ellipseScaleYFactor);

  const ellipse1 = makeEllipse(startX1, startY1);
  const ellipse2 = makeEllipse(startX2, startY2);

  //ellipse outline
  const { outline, outlineEdgeTypes } = openingProfileFromSections([
    { polyLine: ellipse1, edgeType: "CURVED" },
    { polyLine: ellipse2, edgeType: "CURVED" },
  ]);

  return {
    outlineRectangular,
    outline,
    outlineEdgeTypes,
  };
}

function ellipseMaker(centerX: number, centerY: number, ellipseScaleXFactor: number, ellipseScaleYFactor: number) {
  return function (startPtX: number, startPtY: number): PolyLine2 {
    const ellipsePl = arc2ByCenterStartAngle(
      centerX,
      centerY,
      startPtX,
      startPtY,
      PI,
      WINDOW_CHORD_TOLERANCE,
      WINDOW_ARC_MIN_DIVS,
      WINDOW_ARC_MAX_DIVS,
    );
    polyLine2NonUniformScaleAndKeepCenter(ellipsePl, ellipseScaleXFactor, ellipseScaleYFactor);
    return ellipsePl;
  };
}

export function polygonalWindowShapes(
  descriptor: Pick<WindowDescriptor, "polygonWindowMode" | "height" | "width" | "angleVerticalOffset">,
  outlineRectangular: Rect2,
  mirror: boolean,
): OpeningShapes {
  const { polygonWindowMode, height } = descriptor;

  const oriented = profile2OrientCopy(outlineRectangular);

  const verticalOffset = windowConstrainAngleVerticalOffset(descriptor);

  let outline = polyLine2New([]);

  switch (polygonWindowMode) {
    case PolygonWindowMode.PENTAGONAL:
    case undefined:
      const aabb2 = polyLine2AABB2(oriented);
      outline = polyLine2New([
        oriented[0],
        oriented[1], // bottom left
        oriented[2],
        oriented[3], // bottom right
        oriented[4],
        oriented[5] - verticalOffset, // angle right
        lerp(aabb2.minX, aabb2.maxX, 0.5),
        oriented[5], // top center
        oriented[0],
        oriented[5] - verticalOffset, // angle left
        oriented[0],
        oriented[1], // bottom left
      ]);
      break;
    case PolygonWindowMode.TRAPEZOIDAL_RIGHT:
      outline = polyLine2New([
        oriented[0],
        oriented[1], // bottom left
        oriented[2],
        oriented[3], // bottom right
        oriented[4],
        oriented[5] - verticalOffset, // low right
        oriented[6],
        oriented[7], // top right
        oriented[0],
        oriented[1], // bottom left
      ]);
      break;
    case PolygonWindowMode.TRAPEZOIDAL_LEFT:
      outline = polyLine2New([
        oriented[0],
        oriented[1], // bottom left
        oriented[2],
        oriented[3], // bottom right
        oriented[2],
        oriented[3] + height, // top right
        oriented[0],
        oriented[1] + height - verticalOffset, // top left
        oriented[0],
        oriented[1], // bottom left
      ]);
      break;
  }

  if (mirror) {
    outline = profile2OrientCopy(mirrorProfile2Copy(outline));
  }

  const profile = openingProfileFromSections([{ polyLine: outline, edgeType: "STRAIGHT" }]);

  return {
    outlineRectangular,
    outline: profile.outline,
    outlineEdgeTypes: profile.outlineEdgeTypes,
  };
}

interface ArchGeomInfo {
  arcCurve: PolyLine2;
  arcRadius: number;
  arcAngleRads: number;
  arcCenterX: number;
  arcCenterY: number;
  arcHeight: number;
  arcStartX: number;
  arcStartY: number;
  arcEndX: number;
  arcEndY: number;
}

export function archGeom(
  descriptor: Pick<WindowDescriptor, "archRadius" | "shape">,
  exteriorOutline: PolyLine2,
): ArchGeomInfo {
  //overall dims
  const width = polyLine2EdgeLength(exteriorOutline, 2);
  const halfWidth = width / 2;
  const height = polyLine2EdgeLength(exteriorOutline, 1);

  //arc dims
  let arcRadius = Math.max(descriptor.archRadius ?? halfWidth, halfWidth); //the minimum allowed radius is half the width
  let arcAngleRads = Math.asin(halfWidth / arcRadius);
  const archHeight = arcRadius * (1 - Math.cos(arcAngleRads));

  //arc bounding box
  const topBox = polyLine2ThickenedEdge(exteriorOutline, 2, -archHeight);
  polyLine2Shift(topBox, 2);
  const leftEdge = polyLine2Edge(topBox, 3);
  const rightEdge = polyLine2Edge(topBox, 1);
  const arcBar = polyLine2Edge(topBox, 0);
  const arcHeight = polyLine2EdgeLength(topBox, 3);

  //make arc curve
  const [arcX, arcY] = vec2Lerp(arcBar[0], arcBar[1], arcBar[2], arcBar[3], 0.5);
  const [arcCenterX, arcCenterY] = getArcCenter(arcBar[0], arcBar[1], arcX, arcY + arcHeight, arcBar[2], arcBar[3]);

  arcRadius = vec2Dist(arcBar[0], arcBar[1], arcX, arcCenterY);
  arcAngleRads = 2 * Math.asin(Math.abs(arcBar[0] - arcX) / arcRadius);

  const arcCurve = arc2ByCenterStartAngle(
    arcX,
    arcCenterY,
    arcBar[0],
    arcBar[1],
    arcAngleRads,
    WINDOW_CHORD_TOLERANCE,
    WINDOW_ARC_MIN_DIVS,
    WINDOW_ARC_MAX_DIVS,
  );
  const arcAABB = polyLine2AABB2(arcCurve);
  const arcShift = polyLine2EdgeLength(leftEdge, 0) - (arcAABB.maxY - arcAABB.minY);
  polyLine2Move(arcCurve, 0, arcShift - DIST_EPSILON);

  //make arch underside for add on arched windows
  if (descriptor.shape === WindowShapeTypes.RECTANGLE) {
    let archRail = arcCurve;

    if (height > arcHeight) {
      archRail = polyLine2ConcatAll(
        [[exteriorOutline[2], exteriorOutline[3]], archRail, [exteriorOutline[0], exteriorOutline[1]]],
        false,
      );
    }
  }

  return {
    arcCurve,
    arcRadius,
    arcAngleRads,
    arcCenterX,
    arcCenterY,
    arcHeight,
    arcStartX: leftEdge[2],
    arcStartY: leftEdge[3],
    arcEndX: rightEdge[0],
    arcEndY: rightEdge[1],
  };
}
